import { useQuery } from '@tanstack/react-query'
import { getCustomerDetail } from '@/services/api'
import type { CustomerDetail } from '@/types/salesOrder'

/**
 * The addresses and contacts the ERP holds for one customer, offered as presets on the
 * validation form.
 *
 * They fill the form in; they are not what the order points at. The order carries an address and
 * a contact of its own, extracted from the customer's document and corrected by hand, and that is
 * what the ERP is sent. Picking a preset is a way of not typing one out, and an order filled in
 * from a preset is indistinguishable afterwards from one typed by hand.
 *
 * Keyed on the customer the form is currently pointed at rather than on the one the order was
 * saved against, so changing the company changes what is on offer without a save. The customer
 * the order already has arrives with the order, so that first read costs nothing.
 */
export const customerPresetQueryKeys = {
  detail: (workflowId: string, customerId: string) =>
    ['customer-detail', workflowId, customerId] as const,
}

/** Long enough that working down an order does not re-ask; these move at the pace of an ERP sync. */
const PRESETS_FRESH_FOR_MS = 5 * 60_000

export function useCustomerDetail(
  workflowId: string | undefined,
  customerId: string,
  /** The customer that came with the order, used in place of a request when it is the same one. */
  known?: CustomerDetail | null
) {
  return useQuery({
    queryKey: customerPresetQueryKeys.detail(workflowId ?? '', customerId),
    queryFn: () => getCustomerDetail(workflowId ?? '', customerId),
    enabled: Boolean(workflowId) && customerId !== '',
    initialData: known && known.id === customerId ? known : undefined,
    staleTime: PRESETS_FRESH_FOR_MS,
  })
}
